import type { PokemonV2 } from './pokemon';
import type { PokemonType } from '../types/type';
import {
  POKEMON_TYPE_NORMAL,
  POKEMON_TYPE_FIGHTING,
  POKEMON_TYPE_FLYING,
  POKEMON_TYPE_POISON,
  POKEMON_TYPE_GROUND,
  POKEMON_TYPE_ROCK,
  POKEMON_TYPE_BUG,
  POKEMON_TYPE_GHOST,
  POKEMON_TYPE_STEEL,
  POKEMON_TYPE_FIRE,
  POKEMON_TYPE_WATER,
  POKEMON_TYPE_GRASS,
  POKEMON_TYPE_ELECTRIC,
  POKEMON_TYPE_PSYCHIC,
  POKEMON_TYPE_ICE,
  POKEMON_TYPE_DRAGON,
  POKEMON_TYPE_DARK,
  POKEMON_TYPE_FAIRY,
} from '../types/type';
import type { POKEMON_ITEM_ID } from '../types/item';
import { getPlateItemByType } from './PlateUtils';

export const ARCEUS_SPECIES_ID = 493;
// アルセウス各フォルムのIDは「基礎ID + タイプID」の形式を想定
export const ARCEUS_FORM_ID_OFFSET = 49300;

export const ARCEUS_SUPPORTED_TYPE_IDS: PokemonType[] = [
  POKEMON_TYPE_NORMAL,
  POKEMON_TYPE_FIGHTING,
  POKEMON_TYPE_FLYING,
  POKEMON_TYPE_POISON,
  POKEMON_TYPE_GROUND,
  POKEMON_TYPE_ROCK,
  POKEMON_TYPE_BUG,
  POKEMON_TYPE_GHOST,
  POKEMON_TYPE_STEEL,
  POKEMON_TYPE_FIRE,
  POKEMON_TYPE_WATER,
  POKEMON_TYPE_GRASS,
  POKEMON_TYPE_ELECTRIC,
  POKEMON_TYPE_PSYCHIC,
  POKEMON_TYPE_ICE,
  POKEMON_TYPE_DRAGON,
  POKEMON_TYPE_DARK,
  POKEMON_TYPE_FAIRY,
];

const ARCEUS_TYPE_SLUG_MAP: Record<number, string> = {
  [POKEMON_TYPE_NORMAL]: 'normal',
  [POKEMON_TYPE_FIGHTING]: 'fighting',
  [POKEMON_TYPE_FLYING]: 'flying',
  [POKEMON_TYPE_POISON]: 'poison',
  [POKEMON_TYPE_GROUND]: 'ground',
  [POKEMON_TYPE_ROCK]: 'rock',
  [POKEMON_TYPE_BUG]: 'bug',
  [POKEMON_TYPE_GHOST]: 'ghost',
  [POKEMON_TYPE_STEEL]: 'steel',
  [POKEMON_TYPE_FIRE]: 'fire',
  [POKEMON_TYPE_WATER]: 'water',
  [POKEMON_TYPE_GRASS]: 'grass',
  [POKEMON_TYPE_ELECTRIC]: 'electric',
  [POKEMON_TYPE_PSYCHIC]: 'psychic',
  [POKEMON_TYPE_ICE]: 'ice',
  [POKEMON_TYPE_DRAGON]: 'dragon',
  [POKEMON_TYPE_DARK]: 'dark',
  [POKEMON_TYPE_FAIRY]: 'fairy',
};

export function isArceusFormId(pokemonId: number): boolean {
  if (pokemonId === ARCEUS_SPECIES_ID) {
    return true;
  }
  const inferredType = pokemonId - ARCEUS_FORM_ID_OFFSET;
  return ARCEUS_SUPPORTED_TYPE_IDS.includes(inferredType as PokemonType);
}

export function getArceusTypeIdFromId(pokemonId: number): PokemonType | null {
  if (pokemonId === ARCEUS_SPECIES_ID) {
    return POKEMON_TYPE_NORMAL;
  }
  const candidate = pokemonId - ARCEUS_FORM_ID_OFFSET;
  if (ARCEUS_SUPPORTED_TYPE_IDS.includes(candidate as PokemonType)) {
    return candidate as PokemonType;
  }
  return null;
}

export function getArceusTypeIdFromPokemon(pokemon?: Pick<PokemonV2, 'id' | 'species_id' | 'type_id_list'> | null): PokemonType | null {
  if (!pokemon) {
    return null;
  }
  if (pokemon.species_id !== ARCEUS_SPECIES_ID && pokemon.id !== ARCEUS_SPECIES_ID && !isArceusFormId(pokemon.id)) {
    return null;
  }
  if (pokemon.type_id_list && pokemon.type_id_list.length > 0) {
    return pokemon.type_id_list[0] as PokemonType;
  }
  return getArceusTypeIdFromId(pokemon.id);
}

export function isArceusNonNormal(pokemon?: Pick<PokemonV2, 'id' | 'species_id' | 'type_id_list'> | null): boolean {
  const typeId = getArceusTypeIdFromPokemon(pokemon);
  if (typeId == null) {
    return false;
  }
  return typeId !== POKEMON_TYPE_NORMAL;
}

export function getArceusPictureUrl(typeId: PokemonType): string {
  const slug = ARCEUS_TYPE_SLUG_MAP[typeId] ?? 'normal';
  if (slug === 'normal') {
    return 'https://img.pokemondb.net/sprites/home/normal/arceus.png';
  }
  return `https://img.pokemondb.net/sprites/home/normal/arceus-${slug}.png`;
}

export function getPlateItemIdForArceusType(typeId: PokemonType): POKEMON_ITEM_ID | null {
  if (typeId === POKEMON_TYPE_NORMAL) {
    return null;
  }
  return getPlateItemByType(typeId);
}

export function getPlateItemIdForArceusPokemon(pokemon?: Pick<PokemonV2, 'id' | 'species_id' | 'type_id_list'> | null): POKEMON_ITEM_ID | null {
  const typeId = getArceusTypeIdFromPokemon(pokemon);
  if (typeId == null) {
    return null;
  }
  return getPlateItemIdForArceusType(typeId);
}
